import { PROTOCOL_VERSION, type ClientMessage, type ServerMessage } from "@hoa/protocol";
import { signChallenge, type Account } from "./adapters/account.js";
import { setClock } from "./adapters/audio.js";

/** Connexion à une session. Reconnexion automatique avec repli progressif. */
/**
 * Jeton de reprise, conservé le temps de l'onglet. Fermer la page par erreur
 * ne doit pas coûter sa place dans la tribu.
 */
function cleReprise(sessionId: string): string {
  return `reprise:${sessionId}`;
}

export function lireJetonReprise(sessionId: string): string | null {
  try {
    return sessionStorage.getItem(cleReprise(sessionId));
  } catch {
    return null;
  }
}

export function ecrireJetonReprise(sessionId: string, token: string): void {
  try {
    sessionStorage.setItem(cleReprise(sessionId), token);
  } catch {
    /* navigation privée : on se passera de la reprise */
  }
}

/**
 * Dernière tribu rejointe pendant cette visite. Permet de proposer « revenir
 * dans ma tribu » depuis l'accueil quand on a quitté la page par mégarde.
 * Comme le jeton, cela vit dans le sessionStorage : rien ne survit à l'onglet.
 */
export interface TribuEnCours {
  sessionId: string;
  nom: string;
  avecCompte: boolean;
}

const CLE_EN_COURS = "reprise:en-cours";

export function lireTribuEnCours(): TribuEnCours | null {
  try {
    const brut = sessionStorage.getItem(CLE_EN_COURS);
    if (!brut) return null;
    const v = JSON.parse(brut) as TribuEnCours;
    return v && typeof v.sessionId === "string" && lireJetonReprise(v.sessionId) ? v : null;
  } catch {
    return null;
  }
}

export function ecrireTribuEnCours(v: TribuEnCours): void {
  try {
    sessionStorage.setItem(CLE_EN_COURS, JSON.stringify(v));
  } catch {
    /* navigation privée */
  }
}

export function oublierTribuEnCours(sessionId?: string): void {
  try {
    sessionStorage.removeItem(CLE_EN_COURS);
    if (sessionId) sessionStorage.removeItem(cleReprise(sessionId));
  } catch {
    /* rien à faire */
  }
}

export class SessionSocket {
  private ws: WebSocket | null = null;
  private closedByUs = false;
  private attempt = 0;
  private reveilBranche = false;
  private samples: Array<{ rtt: number; offset: number }> = [];
  private pinger: number | null = null;

  constructor(
    private sessionId: string,
    private handlers: {
      onMessage: (msg: ServerMessage) => void;
      onOpen?: () => void;
      onClose?: () => void;
    },
    private account: Account | null = null,
    private mode: "participant" | "collective" = "participant",
    /**
     * Les reçus signés gardés par cet appareil. Ils servent au serveur à
     * établir un compte de rencontres vérifiable, puis sont jetés : c'est le
     * seul moyen de montrer « six soirées » derrière un pseudo sans que
     * personne tienne d'historique.
     */
    private receipts: unknown[] = [],
  ) {}

  connect(): void {
    const proto = location.protocol === "https:" ? "wss" : "ws";
    const url = `${proto}://${location.host}/ws?session=${encodeURIComponent(this.sessionId)}${this.mode === "collective" ? "&mode=collective" : ""}`;
    const ws = new WebSocket(url);
    this.ws = ws;

    ws.onopen = () => {
      this.attempt = 0;
      this.send({
        type: "hello",
        protocol: PROTOCOL_VERSION,
        sessionId: this.sessionId,
        pubkey: this.account?.publicKeyHex,
        receipts: this.account && this.receipts.length > 0 ? this.receipts.slice(0, 200) : undefined,
        resume: lireJetonReprise(this.sessionId) ?? undefined,
      });
      this.startPing();
      this.handlers.onOpen?.();
    };

    ws.onmessage = (ev) => {
      const msg = JSON.parse(String(ev.data)) as ServerMessage;
      if (msg.type === "challenge" && this.account) {
        this.send({ type: "auth", signature: signChallenge(this.account, msg.nonce) });
        return;
      }
      if (msg.type === "welcome") {
        ecrireJetonReprise(this.sessionId, msg.resumeToken);
      }
      if (msg.type === "pong") {
        const rtt = Date.now() - msg.clientMs;
        this.samples.push({ rtt, offset: msg.serverMs + rtt / 2 - Date.now() });
        setClock(this.samples.slice(-5));
        return;
      }
      this.handlers.onMessage(msg);
    };

    ws.onclose = () => {
      this.stopPing();
      this.handlers.onClose?.();
      if (this.closedByUs) return;
      const delay = Math.min(8000, 500 * 2 ** this.attempt++);
      window.setTimeout(() => this.connect(), delay);
    };

    this.surveillerLeReveil();
  }

  /**
   * Un téléphone qui se verrouille ferme la connexion, et le navigateur gèle
   * les minuteries : la reconnexion programmée peut ne jamais partir. On se
   * rebranche donc dès que l'écran revient, sans attendre le prochain essai.
   */
  private surveillerLeReveil(): void {
    if (this.reveilBranche) return;
    this.reveilBranche = true;
    const reprendre = (): void => {
      if (this.closedByUs) return;
      if (document.hidden) return;
      const etat = this.ws?.readyState;
      if (etat === WebSocket.OPEN || etat === WebSocket.CONNECTING) return;
      this.attempt = 0;
      this.connect();
    };
    document.addEventListener("visibilitychange", reprendre);
    window.addEventListener("pageshow", reprendre);
    window.addEventListener("online", reprendre);
  }

  private startPing(): void {
    this.stopPing();
    const beat = () => this.send({ type: "ping", clientMs: Date.now() });
    beat();
    this.pinger = window.setInterval(beat, 10_000);
  }

  private stopPing(): void {
    if (this.pinger !== null) window.clearInterval(this.pinger);
    this.pinger = null;
  }

  send(msg: ClientMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  close(): void {
    this.closedByUs = true;
    this.stopPing();
    this.ws?.close();
  }
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { "content-type": "application/json", ...(init?.headers ?? {}) },
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return (await res.json()) as T;
}
